import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  advertisingGrowthIntelligenceApi,
  type AdRecommendation,
  type AdSimulationResult,
  type AdTimelineEvent,
  type AttributionResult,
  type CampaignHealthScore,
  type CampaignVersion,
  type CompetitorAdGap,
  type ExperimentLink,
  type KeywordInsight,
  type OpportunityInsight,
} from '../../api/advertising-growth-intelligence';

type Theme = 'light' | 'dark';
type Tab = 'health' | 'recommendations' | 'keywords' | 'opportunities' | 'attribution' | 'competitor' | 'versions' | 'experiments' | 'timeline';

const DEFAULT_CAMPAIGN = 'CAMP-SP-001';

const priorityCls = (p: string) =>
  p === 'critical' || p === 'high' ? 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200'
    : p === 'medium' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
    : 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300';

function Skeleton({ className = 'h-20' }: { className?: string }) {
  return <div className={`animate-pulse rounded-xl bg-slate-200 dark:bg-slate-800 ${className}`} />;
}

export function AdvertisingGrowthIntelligencePage() {
  const { campaignId: routeCampaignId } = useParams<{ campaignId: string }>();
  const campaignId = routeCampaignId ?? DEFAULT_CAMPAIGN;

  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('health');
  const [health, setHealth] = useState<CampaignHealthScore | null>(null);
  const [recommendations, setRecommendations] = useState<AdRecommendation[]>([]);
  const [keywords, setKeywords] = useState<KeywordInsight[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityInsight[]>([]);
  const [attribution, setAttribution] = useState<AttributionResult[]>([]);
  const [gaps, setGaps] = useState<CompetitorAdGap[]>([]);
  const [versions, setVersions] = useState<CampaignVersion[]>([]);
  const [experiments, setExperiments] = useState<ExperimentLink[]>([]);
  const [timeline, setTimeline] = useState<AdTimelineEvent[]>([]);
  const [simulation, setSimulation] = useState<AdSimulationResult | null>(null);
  const [simulating, setSimulating] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const card = dark ? 'border-violet-900/40 bg-zinc-900' : 'border-violet-200 bg-white';
  const muted = dark ? 'text-zinc-400' : 'text-slate-500';
  const shell = dark ? 'bg-zinc-950 text-zinc-100' : 'bg-violet-50/20 text-slate-900';
  const accent = dark ? 'text-violet-300' : 'text-violet-700';
  const tabActive = 'bg-violet-600 text-white';

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [h, recs, kws, opps, attrs, comp, vers, exps, tl] = await Promise.all([
        advertisingGrowthIntelligenceApi.health(campaignId),
        advertisingGrowthIntelligenceApi.recommendations(campaignId),
        advertisingGrowthIntelligenceApi.keywords(campaignId),
        advertisingGrowthIntelligenceApi.opportunities(campaignId),
        advertisingGrowthIntelligenceApi.attribution(campaignId),
        advertisingGrowthIntelligenceApi.competitor(campaignId),
        advertisingGrowthIntelligenceApi.versions(campaignId),
        advertisingGrowthIntelligenceApi.experiments(campaignId),
        advertisingGrowthIntelligenceApi.history(campaignId),
      ]);
      setHealth(h);
      setRecommendations(recs);
      setKeywords(kws);
      setOpportunities(opps);
      setAttribution(attrs);
      setGaps(comp);
      setVersions(vers);
      setExperiments(exps);
      setTimeline(tl);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load advertising intelligence');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { refresh(); }, [refresh]);

  const runSimulation = async (recId: string) => {
    setSimulating(recId);
    setSimulation(null);
    try {
      const result = await advertisingGrowthIntelligenceApi.simulate(campaignId, recId);
      setSimulation(result);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Simulation failed');
    } finally {
      setSimulating(null);
    }
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'health', label: 'Health' },
    { id: 'recommendations', label: 'Optimization Center' },
    { id: 'keywords', label: 'Keywords' },
    { id: 'opportunities', label: 'Opportunities' },
    { id: 'attribution', label: 'Attribution' },
    { id: 'competitor', label: 'Competitor' },
    { id: 'versions', label: 'Versions' },
    { id: 'experiments', label: 'Experiments' },
    { id: 'timeline', label: 'Timeline' },
  ];

  return (
    <div className={`space-y-4 rounded-xl p-1 ${shell}`} role="main">
      <header className={`rounded-xl border p-4 ${card}`}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>DEV-000107 · Advertising Growth Intelligence · Observe → Learn → Improve</p>
            <h1 className="text-xl font-semibold">{health?.campaign_name ?? 'Campaign'} — {campaignId}</h1>
            {health && (
              <div className={`mt-2 flex flex-wrap gap-x-4 text-sm ${muted}`}>
                <span>Health Score <strong className={accent}>{health.overall_score}/100</strong></span>
                <span>{health.dna_origin ? 'DNA-backed' : 'Pending DNA'}</span>
                <span>{recommendations.length} recommendations</span>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Link to="/amazon/advertising" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Advertising Intel</Link>
            <Link to="/platform/advertising-growth-intelligence-dev107" className={`rounded-lg border px-3 py-1.5 text-sm ${card} ${accent}`}>Sprint Center</Link>
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>{dark ? 'Light' : 'Dark'}</button>
          </div>
        </div>
      </header>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>{t.label}</button>
        ))}
      </div>

      {tab === 'health' && (
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? <Skeleton /> : health?.components.map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <p className="font-semibold">{c.name}</p>
                {c.priority && <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(c.priority)}`}>{c.priority}</span>}
              </div>
              <p className={`mt-1 text-2xl font-bold ${accent}`}>{c.current_value}<span className={`text-sm font-normal ${muted}`}> / {c.target}</span></p>
              {c.gap && <p className={`text-xs ${muted}`}>Gap: {c.gap} · {c.business_impact}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {loading ? <Skeleton /> : recommendations.map((r) => (
            <div key={r.id} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(r.priority)}`}>{r.priority}</span>
                  <h3 className="mt-1 font-semibold">{r.title}</h3>
                  <p className={`text-sm ${muted}`}>{r.business_objective}</p>
                  {r.dna_evidence && <span className={`mt-1 inline-block text-xs ${accent}`}>✓ DNA evidence</span>}
                </div>
                <div className={`text-right text-xs ${muted}`}>
                  <p>Confidence {(r.confidence * 100).toFixed(0)}%</p>
                  {r.expected_profit && <p>Profit {r.expected_profit}</p>}
                  {r.expected_roas && <p>ROAS {r.expected_roas}</p>}
                </div>
              </div>
              {r.evidence && (
                <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{r.evidence.map((e) => <li key={e}>{e}</li>)}</ul>
              )}
              <button
                type="button"
                disabled={simulating === r.id}
                onClick={() => runSimulation(r.id)}
                className="mt-3 rounded-lg bg-violet-600 px-3 py-1.5 text-xs text-white hover:bg-violet-500 disabled:opacity-50"
              >
                {simulating === r.id ? 'Simulating…' : r.action_label}
              </button>
            </div>
          ))}
          {simulation && (
            <div className={`rounded-xl border border-violet-300 p-4 ${card}`}>
              <h3 className="font-semibold">Simulation Result</h3>
              <div className={`mt-2 grid gap-2 text-sm sm:grid-cols-2 ${muted}`}>
                <p>ROAS: <strong className={accent}>{simulation.estimated_roas}</strong></p>
                <p>ACOS: <strong className={accent}>{simulation.estimated_acos}</strong></p>
                <p>Profit: <strong className={accent}>{simulation.estimated_profit}</strong></p>
                <p>Confidence: <strong className={accent}>{(simulation.confidence * 100).toFixed(0)}%</strong></p>
              </div>
              {simulation.key_assumptions && (
                <ul className={`mt-2 list-disc pl-5 text-xs ${muted}`}>{simulation.key_assumptions.map((a) => <li key={a}>{a}</li>)}</ul>
              )}
            </div>
          )}
        </div>
      )}

      {tab === 'keywords' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : keywords.map((k) => (
            <div key={k.keyword} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 ${card}`}>
              <div>
                <p className="font-medium">{k.keyword}</p>
                <p className={`text-xs ${muted}`}>{k.match_type} · {k.intent} · Score {k.score}</p>
              </div>
              <div className={`text-right text-xs ${muted}`}>
                <p>ROAS {k.roas} · {k.profit}</p>
                <p className={accent}>{k.opportunity}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : opportunities.map((o) => (
            <div key={o.id} className={`rounded-xl border p-3 ${card}`}>
              <div className="flex items-center justify-between">
                <p className="font-medium">{o.title}</p>
                <span className={`rounded px-2 py-0.5 text-xs ${priorityCls(o.priority)}`}>{o.priority}</span>
              </div>
              <p className={`text-xs ${muted}`}>{o.type} · {o.expected_profit} · {(o.confidence * 100).toFixed(0)}% confidence</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'attribution' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : attribution.map((a) => (
            <div key={a.factor} className={`flex flex-wrap items-center justify-between gap-2 rounded-xl border p-3 ${card}`}>
              <div>
                <p className="font-medium">{a.factor}</p>
                <p className={`text-xs ${muted}`}>{a.data_type === 'measured' ? 'Measured' : 'Modeled estimate'} · {(a.confidence * 100).toFixed(0)}% confidence</p>
              </div>
              <div className={`text-right text-xs ${muted}`}>
                <p>Contribution {a.contribution}</p>
                <p className={accent}>{a.profit_impact}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'competitor' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : gaps.map((g) => (
            <div key={g.dimension} className={`flex items-center justify-between rounded-xl border p-3 ${card}`}>
              <span className="font-medium">{g.dimension}</span>
              <span className={`font-semibold ${g.gap.startsWith('-') ? 'text-red-600' : 'text-emerald-600'}`}>{g.gap}</span>
              <span className={`text-xs ${muted}`}>{g.our_value} vs {g.competitor_value} ({g.data_type})</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'versions' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : versions.map((v) => (
            <div key={v.id} className={`flex items-center justify-between rounded-xl border p-3 ${card}`}>
              <div>
                <p className="font-medium">v{v.version} — {v.status}</p>
                <p className={`text-xs ${muted}`}>{v.summary} · {v.author}</p>
              </div>
              <span className={v.approved ? 'text-emerald-600' : 'text-violet-600'}>{v.approved ? 'Approved' : 'Draft'}</span>
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="space-y-2">
          {loading ? <Skeleton /> : experiments.map((e) => (
            <div key={e.id} className={`rounded-xl border p-3 ${card}`}>
              <p className="font-medium">{e.name}</p>
              <p className={`text-xs ${muted}`}>{e.status} · {e.baseline_kpi} · {e.observation_window}</p>
            </div>
          ))}
        </div>
      )}

      {tab === 'timeline' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          {loading ? <Skeleton /> : timeline.map((e) => (
            <div key={e.id} className="flex gap-3 border-b py-2 last:border-0 dark:border-slate-800">
              <time className={`shrink-0 text-xs ${muted}`}>{new Date(e.timestamp).toLocaleString()}</time>
              <span className="text-sm">{e.title}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
